// Cart Drawer web component
class CartDrawer extends DrawerSimple {
  // Defines which CSS selectors should be updated during a "light" (partial) render.
  // This prevents the entire drawer from flashing when only small numbers (like totals) change.
  static get LIGHT_SELECTORS() {
    return [
      '.js-cart-drawer-header',
      '.js-drawer-cart-items-wrapper',
      '.js-free-shipping-progress',
      '.cart-drawer__totals',
      '.js-extra-services-minicart',
      '.cart-drawer-discount-input',
      '.manipulation-fee-block',
    ];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    // Initialization logic for the cart drawer...
  }

  /**
   * Null-safe extraction: returns inner HTML of `selector` within `html`, or null.
   * @param {string} html - The HTML string to parse
   * @param {string} selector - The CSS selector to query
   * @returns {string|null}
   */
  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : null;
  }

  /**
   * Apply a re-rendered cart-drawer section to the live DOM.
   * @param {object} parsedState - The parsed state object from Shopify cart API
   * @param {boolean} partial - true to update only light selectors (in-place); false to replace the whole drawer body
   */
  applySections(parsedState, partial) {
    const drawerHTML = parsedState.sections?.[getCartDrawerSectionId()];

    if (partial) {
      const parsedDoc = drawerHTML ? new DOMParser().parseFromString(drawerHTML, 'text/html') : null;
      CartDrawer.LIGHT_SELECTORS.forEach((sel) => {
        const target = this.querySelector(sel);
        const source = parsedDoc?.querySelector(sel);

        if (sel === '.js-extra-services-minicart') {
          return;
        }

        if (sel === '.manipulation-fee-block') {
          if (target && source) {
            target.replaceWith(source);
          } else if (!target && source) {
            this.querySelector('.cart-summary__totals')?.prepend(source);
          } else if (target && !source) {
            target.classList.add('hidden');
          }
          return;
        }

        if (target && source) {
          if (sel === '.cart-drawer-discount-input' || target.tagName === 'CART-DRAWER-DISCOUNT-INPUT') {
            target.replaceWith(source);
          } else {
            target.innerHTML = source.innerHTML;
          }
        }
      });
    } else {
      const inner = this.getSectionInnerHTML(drawerHTML, 'cart-drawer');
      if (inner !== null) this.innerHTML = inner;
    }
  }

  /**
   * Overrides DrawerSimple's open method. Ensures accessibility is set up
   * for the cart note and wishlist state is updated before sliding open.
   * @param {HTMLElement} triggeredBy - The element that triggered the open action
   */
  open(triggeredBy) {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('aria-controls')) this.setSummaryAccessibility(cartDrawerNote);

    if (window.updateWishlistStateImmediately) {
      window.updateWishlistStateImmediately(this);
    }

    super.open(triggeredBy);
  }

  /**
   * Configures ARIA attributes and keyboard/click listeners for the cart note dropdown,
   * ensuring screen readers can announce it and keyboard users can toggle it.
   * @param {HTMLElement} cartDrawerNote - The summary element for the cart note
   */
  setSummaryAccessibility(cartDrawerNote) {
    const contentElement = cartDrawerNote.nextElementSibling;
    if (contentElement) {
      if (!contentElement.id) contentElement.id = cartDrawerNote.parentElement.id + '-content';
      cartDrawerNote.setAttribute('aria-controls', contentElement.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Add-to-cart render (invoked by product-form). Full re-render, except when
   * adding from the in-drawer reco slider — then a partial update keeps the
   * slider you're interacting with intact.
   * @param {object} parsedState - The new state of the cart
   * @param {boolean} isCartReco - Whether the add action came from the in-cart recommendations
   */
  renderContents(parsedState, isCartReco = false) {
    const inner = this.querySelector('.drawer-inner');
    const transitioning =
      !inner ||
      inner.classList.contains('is-empty') ||
      !this.querySelector('#CartDrawer-CartItems') ||
      this.querySelector('#CartDrawer-CartItems')?.querySelector('.drawer__cart-items-wrapper') === null;

    const recoCount = this.querySelectorAll('.js-cart-reco .product-carousel__track > .product-carousel__slide').length;
    const lowReco = isCartReco && recoCount < 4;

    if (transitioning || lowReco) {
      this.classList.remove('is-empty');
      inner?.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.applySections(parsedState, isCartReco && !transitioning && !lowReco);

    // Render other external sections (like the cart bubble) requested by this component
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'CartDrawer') return;
      const target =
        document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
      const innerHTML = this.getSectionInnerHTML(parsedState.sections?.[section.section], section.selector);
      if (target && innerHTML !== null) target.innerHTML = innerHTML;
    });

    setTimeout(() => {
      this.open();
    });
  }

  /**
   * Sections requested from the cart AJAX API (used by product-form on add).
   * @returns {Array<{id: string, section: string, selector: string}>}
   */
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: getCartDrawerSectionId(), selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /**
   * Initializes the component.
   */
  constructor() {
    super();
  }

  /**
   * Qty change / remove in the drawer: partial update while items remain
   * (the base handles the empty transition via cartClear → full render).
   * @param {object} parsedState - The new cart state
   */
  renderContents(parsedState) {
    // cart/change.js returns the whole cart (with `items`);
    // cart/add.js (variant swap) returns only the added line item — the cart is non-empty then, so stay partial.
    const remaining = Array.isArray(parsedState.items)
      ? parsedState.items.filter((item) => item.product_type !== 'Extra services').length
      : 1;

    const cartDrawer = this.closest('cart-drawer');
    if (cartDrawer) {
      cartDrawer.applySections(parsedState, remaining > 0);
    }

    // Render other external sections (like the cart bubble) requested by this component
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'CartDrawer') return;
      const target =
        document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
      const innerHTML = this.getSectionInnerHTML(parsedState.sections?.[section.section], section.selector);
      if (target && innerHTML !== null) target.innerHTML = innerHTML;
    });
  }

  /**
   * Sections requested from the cart AJAX API on qty change.
   * @returns {Array<{id: string, section: string, selector: string}>}
   */
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: getCartDrawerSectionId(), selector: '.js-cart-drawer-inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  /**
   * Cross-source cart update (e.g. item added elsewhere): refresh items + totals.
   */
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=${getCartDrawerSectionId()}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const cartDrawer = this.closest('cart-drawer');
        ['cart-drawer-items', '.cart-drawer__totals', '.manipulation-fee-block'].forEach((selector) => {
          const target = cartDrawer?.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      })
      .catch((e) => console.error(e));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
